import React, { Fragment, useEffect, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import closeIcon from "../../assets/close-icon.png";
import { classNames } from "Utils/utils";

const ModalSkeleton = ({
  title,
  children,
  isOpen,
  onClose,
  closeModal,
  modalFooter = null,
  cssClass,
  bodyPadding,
  titleCss = null,
  /**
   * Participant document file preview only — keeps default modals on the original layout.
   * When true (with modalFooter), body fills space between header and footer.
   */
  stretchBody = false,
  overlayZIndex = 10000,
}) => {
  const trackedRef = useRef(false);

  useEffect(() => {
    if (isOpen && !trackedRef.current) {
      trackedRef.current = true;
      try {
        const JEB = require("Utils/JourneyEventBuffer").default;
        JEB.track("modal_open", {
          event_data: {
            modal_title: title || "Untitled",
            route: window.location.pathname,
          },
        });
      } catch (e) {
        // Journey tracking should never break UI
      }
    }
    if (!isOpen) {
      trackedRef.current = false;
    }
  }, [isOpen, title]);

  return (
    <Transition.Root as={Fragment} show={isOpen}>
      <Dialog
        as='div'
        className='relative'
        style={{ zIndex: overlayZIndex }}
        onClose={
          () => {}
          // closeModal
        }
      >
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-gray-800 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-10 overflow-y-auto'>
          <div className='flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            >
              <Dialog.Panel
                className={classNames(
                  "relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all",
                  stretchBody && "flex min-h-0 flex-col",
                  cssClass
                )}
              >
                <div
                  className={classNames(
                    "flex flex-col bg-white",
                    stretchBody ? "min-h-0 w-full flex-1" : "h-full"
                  )}
                >
                  <div
                    className={classNames(
                      " p-4 relative border-b border-gray-400 flex-shrink-0",
                      title === "" && "h-[57px]",
                      titleCss ? titleCss : "bg-gradient"
                    )}
                  >
                    <Dialog.Title
                      as='h3'
                      className={classNames(
                        "text-lg font-semibold pr-4 leading-6 tracking-wider text-white",
                        titleCss !== null && "text-black text-center"
                      )}
                    >
                      {title}
                    </Dialog.Title>
                    {closeModal && (
                      <div
                        className='absolute right-4 top-4 text-white cursor-pointer'
                        onClick={closeModal}
                      >
                        <img
                          src={closeIcon}
                          alt='close icon'
                          className='h-5 max-w-[20px] w-5'
                        />
                      </div>
                    )}
                  </div>
                  <div
                    className={classNames(
                      "p-3 custom-scroll flex-1 min-h-0",
                      stretchBody
                        ? "overflow-hidden max-h-none"
                        : "max-h-[70vh] overflow-y-auto",
                      bodyPadding
                    )}
                  >
                    <div
                      className={
                        stretchBody
                          ? "flex h-full min-h-0 flex-1 flex-col"
                          : "mt-2"
                      }
                    >
                      {children}
                    </div>
                  </div>
                </div>
                {modalFooter && (
                  <div className='bg-gray-50 p-3 border-t border-gray-200 flex justify-end gap-x-2 flex-shrink-0'>
                    {modalFooter}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
export default ModalSkeleton;
